import { ChevronsLeft, ChevronLeft, ChevronRight, Sigma } from 'lucide-react'
import { useDashboardStore } from '../stores/dashboardStore'
import { useDashboardTableTotals } from '../hooks/useDashboardTableTotals'

interface TablePaginationProps {
  /** Antall rader på siden som vises nå (sentinel-raden er allerede kastet). */
  rowCount: number
  /** Kom sentinel-raden tilbake fra RPC-en? Da finnes det en side til. */
  hasNextPage: boolean
}

const nok = (value: number) =>
  value.toLocaleString('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

/**
 * Første / forrige / neste. Ingen sidetall og ingen «siste side» (#934) —
 * begge krever totalen, altså nøyaktig det skannet vi fjernet fra
 * standardveien. «Første» er derimot gratis: det er OFFSET 0.
 */
export function TablePagination({ rowCount, hasNextPage }: TablePaginationProps) {
  const pageIndex = useDashboardStore((s) => s.pageIndex)
  const pageSize = useDashboardStore((s) => s.pageSize)
  const setPageIndex = useDashboardStore((s) => s.setPageIndex)
  const setPageSize = useDashboardStore((s) => s.setPageSize)
  const totalsRequested = useDashboardStore((s) => s.totalsRequested)
  const requestTotals = useDashboardStore((s) => s.requestTotals)

  const {
    data: totals,
    isFetching: totalsLoading,
    isError: totalsError,
    refetch: refetchTotals,
  } = useDashboardTableTotals()

  const isFirstPage = pageIndex === 0
  const from = pageIndex * pageSize + 1
  const to = pageIndex * pageSize + rowCount

  // Linja blir stående også når siden er tom. Er vi forbi første side, er
  // den eneste veien tilbake. Og «Vis totaler» må være trykkbar nettopp når
  // filteret ikke traff noe — det er da operatøren vil ha bekreftet at
  // svaret faktisk er null, ikke bare at skjermen er tom.

  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-[12px] text-gray-500">
      <div className="flex items-center gap-3">
        <span>{rowCount > 0 ? `Viser ${from}–${to}` : 'Ingen rader'}</span>

        {!totalsRequested ? (
          <button
            onClick={requestTotals}
            className="flex items-center gap-1 border-l border-gray-200 pl-3 text-gray-500 hover:text-gray-700"
          >
            <Sigma size={12} />
            Vis totaler
          </button>
        ) : (
          <span className="border-l border-gray-200 pl-3 text-gray-400">
            {totalsLoading && 'Regner ut…'}
            {/* Feilen må ha en vei ut. `totalsRequested` blir stående etter at
                retry-en er brukt opp, så uten denne knappen er «Vis totaler»
                borte og eneste utvei er å endre filteret. */}
            {totalsError && !totalsLoading && (
              <button onClick={() => void refetchTotals()} className="hover:text-gray-700">
                Kunne ikke hente totaler — prøv igjen
              </button>
            )}
            {totals && (
              <>
                <span className="text-gray-600 font-medium tabular-nums">{nok(totals.total_count)}</span> ordre
                <span className="text-gray-300 mx-1.5">·</span>
                Sum:{' '}
                <span className="text-gray-600 font-medium tabular-nums">{nok(totals.total_amount_sum)}</span>
                <span className="text-gray-300 mx-0.5">/</span>
                <span className="text-gray-500 tabular-nums">{nok(totals.total_amount_sum * 1.25)}</span>
                <span className="text-gray-300 ml-0.5">inkl. mva</span>
              </>
            )}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => setPageIndex(0)}
          disabled={isFirstPage}
          aria-label="Første side"
          title="Første side"
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronsLeft size={14} />
        </button>

        <button
          onClick={() => setPageIndex(pageIndex - 1)}
          disabled={isFirstPage}
          aria-label="Forrige side"
          title="Forrige side"
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={14} />
        </button>

        <span className="px-2 tabular-nums text-gray-400">Side {pageIndex + 1}</span>

        <button
          onClick={() => setPageIndex(pageIndex + 1)}
          disabled={!hasNextPage}
          aria-label="Neste side"
          title="Neste side"
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      <select
        value={pageSize}
        onChange={(e) => setPageSize(Number(e.target.value))}
        className="border border-gray-200 rounded-md px-2 py-1 text-[12px] bg-white text-gray-600"
      >
        {[10, 25, 50, 100].map((size) => (
          <option key={size} value={size}>
            {size} per side
          </option>
        ))}
      </select>
    </div>
  )
}
